/**
 * A conversation's bookmarks bar (Slack; logic/bookmarks): links (with the site's favicon), folders (one level deep),
 * files shared in the conversation and Bundle pages; "Add a bookmark" / edit with a link and a name (the host when no
 * name is typed), "Add a folder", "Add a file" / "Add a page" pickers, drag to reorder or into / out of a folder,
 * remove (a folder takes its bookmarks). Members of an unarchived conversation may change it. Other members' changes
 * arrive as channel.updated: when the conversation's record changes, the list is refetched. Online only.
 */
import type { BookmarkRequest, ChannelBookmark } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import {
  bookmarkFileQuery,
  bookmarkKind,
  bookmarkTree,
  canEditBookmarks,
  canMoveBookmark,
  defaultBookmarkTitle,
  moveRank,
  normalizeBookmarkUrl,
  sortBookmarks,
  type BookmarkNode,
} from "../logic/bookmarks.ts";
import { myFeatureAccess } from "../logic/features.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

/** The add / edit sheet: a link (url + name) or a folder (name only). `parentId` = the folder a new link goes in. */
export type BookmarkEditor = { id?: string; kind: "link" | "folder"; title: string; url: string; parentId?: string; problem?: MessageDescriptor; saving: boolean };

export type BookmarkPickItem = { id: string; title: string; mime?: string; icon?: string };

/** "Add a file" / "Add a page": what can be bookmarked, filtered by `query`. */
export type BookmarkPicker = { kind: "file" | "page"; query: string; parentId?: string; loading: boolean; items: BookmarkPickItem[]; failed: boolean };

export type ChannelBookmarksState = {
  bookmarks: ChannelBookmark[];
  /** The bar as shown (logic/bookmarks bookmarkTree). */
  tree: BookmarkNode[];
  canEdit: boolean;
  /** Search can list this conversation's files (not in group DMs). */
  canAddFiles: boolean;
  /** The add / edit sheet; absent = closed. */
  editor?: BookmarkEditor;
  picker?: BookmarkPicker;
};

export class ChannelBookmarksViewModel extends ViewModel<ChannelBookmarksState> {
  private lastChannel: unknown;
  private allPickItems: BookmarkPickItem[] = [];

  constructor(
    private readonly ws: WorkspaceViewModel,
    private readonly channelId: string,
  ) {
    super({ bookmarks: [], tree: [], canEdit: false, canAddFiles: false });
    this.onDispose(ws.subscribe(() => this.onWorkspace()));
    this.onWorkspace();
  }

  refresh(): Promise<void> {
    return this.ws.deps.api.listBookmarks(this.ws.wsId, this.channelId).then(
      (list) => {
        if (!this.isDisposed) this.setList(list);
      },
      () => undefined,
    );
  }

  /** "Add a bookmark" (a link), in the bar or in folder `parentId`. */
  openAdd(parentId?: string): void {
    if (!this.state.canEdit) return;
    const editor: BookmarkEditor = { kind: "link", title: "", url: "", saving: false };
    if (parentId !== undefined) editor.parentId = parentId;
    this.update({ editor });
  }

  /** "Add a folder". */
  openAddFolder(): void {
    if (this.state.canEdit) this.update({ editor: { kind: "folder", title: "", url: "", saving: false } });
  }

  /** Edit: links change name and link; folders, files and pages their name. */
  openEdit(id: string): void {
    const b = this.state.bookmarks.find((x) => x.id === id);
    if (b === undefined || !this.state.canEdit) return;
    this.update({ editor: { id, kind: bookmarkKind(b) === "link" ? "link" : "folder", title: b.title, url: b.url, saving: false } });
  }

  setTitle(title: string): void {
    const e = this.state.editor;
    if (e === undefined) return;
    const { problem: _p, ...rest } = e;
    this.update({ editor: { ...rest, title } });
  }

  setUrl(url: string): void {
    const e = this.state.editor;
    if (e === undefined) return;
    const { problem: _p, ...rest } = e;
    this.update({ editor: { ...rest, url } });
  }

  closeEditor(): void {
    if (this.state.editor === undefined) return;
    const { editor: _e, ...rest } = this.state;
    this.setState(rest);
  }

  async save(): Promise<void> {
    const e = this.state.editor;
    if (e === undefined || e.saving) return;
    const existing = e.id === undefined ? undefined : this.state.bookmarks.find((b) => b.id === e.id);
    const isLink = existing === undefined ? e.kind === "link" : bookmarkKind(existing) === "link";
    let req: BookmarkRequest;
    if (isLink) {
      const url = normalizeBookmarkUrl(e.url);
      if (url === undefined) {
        this.update({ editor: { ...e, problem: msg("bookmarks.badUrl") } });
        return;
      }
      req = { title: e.title.trim() === "" ? defaultBookmarkTitle(url) : e.title.trim(), url };
    } else {
      if (e.title.trim() === "") {
        this.update({ editor: { ...e, problem: msg("bookmarks.nameNeeded") } });
        return;
      }
      req = { title: e.title.trim(), url: existing?.url ?? "" };
      if (existing === undefined) req.kind = "folder";
    }
    if (existing === undefined && e.parentId !== undefined) req.parentId = e.parentId;
    this.update({ editor: { ...e, saving: true } });
    try {
      const api = this.ws.deps.api;
      const b = e.id === undefined ? await api.addBookmark(this.ws.wsId, this.channelId, req) : await api.updateBookmark(this.ws.wsId, this.channelId, e.id, req);
      this.setList([b, ...this.state.bookmarks.filter((x) => x.id !== b.id)]);
      this.closeEditor();
    } catch {
      const now = this.state.editor;
      if (now !== undefined) this.update({ editor: { ...now, saving: false, problem: msg("bookmarks.failed") } });
    }
  }

  /** "Add a file" (files shared in this conversation, newest first) or "Add a page" (pages I can open). */
  openPicker(kind: "file" | "page", parentId?: string): void {
    if (!this.state.canEdit) return;
    const picker: BookmarkPicker = { kind, query: "", loading: true, items: [], failed: false };
    if (parentId !== undefined) picker.parentId = parentId;
    this.allPickItems = [];
    this.update({ picker });
    void this.loadPickItems(kind).then(
      (items) => {
        if (this.state.picker?.kind !== kind) return;
        this.allPickItems = items;
        this.update({ picker: { ...this.state.picker, loading: false, items: this.filtered(this.state.picker.query) } });
      },
      () => {
        if (this.state.picker?.kind === kind) this.update({ picker: { ...this.state.picker, loading: false, failed: true } });
      },
    );
  }

  setPickerQuery(query: string): void {
    const p = this.state.picker;
    if (p !== undefined) this.update({ picker: { ...p, query, items: this.filtered(query) } });
  }

  closePicker(): void {
    if (this.state.picker === undefined) return;
    const { picker: _p, ...rest } = this.state;
    this.setState(rest);
  }

  /** Bookmarks the picked file or page (its name as the title) and closes the picker. */
  async pick(id: string): Promise<void> {
    const p = this.state.picker;
    const item = this.allPickItems.find((i) => i.id === id);
    if (p === undefined || item === undefined) return;
    const req: BookmarkRequest = { title: item.title.slice(0, 200) || "Untitled", url: "", kind: p.kind };
    if (p.kind === "file") {
      req.fileId = item.id;
      if (item.mime !== undefined) req.mime = item.mime;
    } else req.pageId = item.id;
    if (p.parentId !== undefined) req.parentId = p.parentId;
    this.closePicker();
    try {
      const b = await this.ws.deps.api.addBookmark(this.ws.wsId, this.channelId, req);
      this.setList([b, ...this.state.bookmarks.filter((x) => x.id !== b.id)]);
    } catch {
      void this.refresh();
    }
  }

  /**
   * Drag and drop (and "Move to folder"): put `id` at `index` among the bookmarks of `parentId` (absent = the bar),
   * counted without the dragged bookmark. Optimistic; a refused move refetches.
   */
  move(id: string, parentId: string | undefined, index: number): void {
    const list = this.state.bookmarks;
    if (!this.state.canEdit || !canMoveBookmark(list, id, parentId)) return;
    const rank = moveRank(list, id, parentId, index);
    const b = list.find((x) => x.id === id)!;
    const { parentId: _old, ...rest } = b;
    const moved: ChannelBookmark = parentId === undefined ? { ...rest, rank } : { ...rest, rank, parentId };
    this.setList(list.map((x) => (x.id === id ? moved : x)));
    const req = parentId === undefined ? { rank } : { rank, parentId };
    this.ws.deps.api.moveBookmark(this.ws.wsId, this.channelId, id, req).catch(() => void this.refresh());
  }

  /** Puts `id` last in folder `parentId` (absent = last in the bar). */
  moveToFolder(id: string, parentId: string | undefined): void {
    const node = parentId === undefined ? undefined : this.state.tree.find((n) => n.bookmark.id === parentId);
    this.move(id, parentId, parentId === undefined ? this.state.tree.length : (node?.children.length ?? 0));
  }

  /** Removes a bookmark; a folder takes its bookmarks with it (Slack). */
  remove(id: string): void {
    if (!this.state.canEdit) return;
    this.setList(this.state.bookmarks.filter((b) => b.id !== id && b.parentId !== id));
    this.ws.deps.api.deleteBookmark(this.ws.wsId, this.channelId, id).catch(() => void this.refresh());
  }

  private setList(list: ChannelBookmark[]): void {
    const bookmarks = sortBookmarks(list);
    this.update({ bookmarks, tree: bookmarkTree(bookmarks) });
  }

  private filtered(query: string): BookmarkPickItem[] {
    const q = query.trim().toLocaleLowerCase();
    const all = q === "" ? this.allPickItems : this.allPickItems.filter((i) => i.title.toLocaleLowerCase().includes(q));
    return all.slice(0, 50);
  }

  private async loadPickItems(kind: "file" | "page"): Promise<BookmarkPickItem[]> {
    const api = this.ws.deps.api;
    if (kind === "page") {
      const res = await api.listPages(this.ws.wsId);
      return res.pages
        .filter((p) => p.archivedAt === undefined && p.templateId === undefined && p.databaseId === undefined)
        .sort((a, b) => b.updatedAt - a.updatedAt)
        .map((p) => {
          const item: BookmarkPickItem = { id: p.id, title: p.title.trim() === "" ? "Untitled" : p.title };
          if (p.icon !== undefined) item.icon = p.icon;
          return item;
        });
    }
    const query = this.fileQuery();
    if (query === undefined) return [];
    const res = await api.searchMessages(this.ws.wsId, query);
    const seen = new Set<string>();
    const items: BookmarkPickItem[] = [];
    for (const m of res.messages) {
      for (const f of m.files ?? []) {
        if (seen.has(f.id)) continue;
        seen.add(f.id);
        items.push({ id: f.id, title: f.name, mime: f.mime });
      }
    }
    return items;
  }

  private fileQuery(): string | undefined {
    const cache = this.ws.state.cache;
    const c = cache?.channels[this.channelId];
    if (cache === undefined || c === undefined) return undefined;
    let handle: string | undefined;
    if (c.kind === "dm") {
      const others = (c.dmUserIds ?? []).filter((u) => u !== cache.meId);
      handle = cache.members[others[0] ?? cache.meId]?.handle;
    }
    return bookmarkFileQuery(c, handle);
  }

  private onWorkspace(): void {
    const cache = this.ws.state.cache;
    const c = cache?.channels[this.channelId];
    // Not while Chat is off, even for owners and admins who may still look (Settings → Features).
    const chat = cache === undefined ? "full" : myFeatureAccess(cache, "chat", this.ws.deps.clock.now());
    const canEdit = canEditBookmarks(cache?.memberships[this.channelId] !== undefined, c?.archived === true) && chat === "full";
    if (canEdit !== this.state.canEdit) this.update({ canEdit });
    const canAddFiles = c !== undefined && this.fileQuery() !== undefined;
    if (canAddFiles !== this.state.canAddFiles) this.update({ canAddFiles });
    // channel.updated (a bookmark changed, among others): refetch.
    if (c !== undefined && c !== this.lastChannel) {
      this.lastChannel = c;
      void this.refresh();
    }
  }
}
